import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Check, Fingerprint, Hourglass, Lock, LogOut, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Sheet } from "@/components/Sheet";
import { AddKidPasskeySheet } from "@/components/SwitchUser";
import { formatRemaining, StopTimeButton, useSessionClock } from "@/components/ScreenTime";
import { useTaskBurst } from "@/components/TaskBurst";
import { Button, Card, cx, PageLoader } from "@/components/ui";
import { Avatar, Confetti, Points, ProgressBar } from "@/components/bits";
import { api } from "@/lib/api";
import { passkeysSupported } from "@/lib/passkeys";
import { useAction, useBanks, useKidJobs, useKidResponsibilities, useKidSummary, useRedemptions, useRewardTypes, useStore } from "@/lib/queries";
import { useLogout, useSession } from "@/lib/session";
import { isSpeechMuted, setSpeechMuted, speak } from "@/lib/speech";
import { formatDuration } from "@shared/duration";
import { isConvertible, pointsForUnits } from "@shared/economy";
import { AVATARS } from "@shared/templates";
import type { BankDTO, KidJobDTO, KidResponsibilityDTO, RewardTypeDTO, StoreItemDTO } from "@shared/types";
import { useUnlockCelebration } from "./common";

/**
 * Screens for kids who may not read yet: big pictures, one tap, everything read aloud.
 * No typing, prices in words, or history. Parents still approve whatever needs approving.
 */

const TILE = "relative flex aspect-square flex-col items-center justify-center gap-1 rounded-[2rem] p-2 text-center shadow-card ring-1 ring-line/60 transition active:scale-95";

function TileLabel({ children }: { children: string }) {
  return <span className="line-clamp-2 text-base font-extrabold leading-tight">{children}</span>;
}

function TileMark({ className, children }: { className: string; children: ReactNode }) {
  return <span className={cx("absolute right-2 top-2 flex size-10 items-center justify-center rounded-full text-white", className)}>{children}</span>;
}

function Heading({ children }: { children: string }) {
  return <h2 className="mb-3 mt-7 px-1 text-2xl font-black">{children}</h2>;
}

// ---------- today ----------

function ResponsibilityTile({ item }: { item: KidResponsibilityDTO }) {
  const r = item.responsibility;
  const status = item.completion?.status;
  const checked = status === "done" || status === "approved";
  const waiting = status === "done" && !item.counts;
  const burst = useTaskBurst();
  const complete = useAction(() => api.post(`/responsibilities/${r.id}/complete`));
  const undo = useAction(() => api.post(`/responsibilities/${r.id}/uncomplete`));
  // Show the tap right away; both mutations stay pending until the fresh list has loaded.
  const showChecked = complete.isPending || (checked && !undo.isPending);
  return (
    <button
      type="button"
      aria-pressed={checked}
      disabled={complete.isPending || undo.isPending || status === "approved"}
      onClick={(e) => {
        if (checked) {
          speak(r.title);
          undo.mutate();
        } else {
          speak(`${r.title}. Good job!`);
          burst(e.currentTarget);
          complete.mutate();
        }
      }}
      className={cx(TILE, showChecked ? "bg-brand-soft" : status === "rejected" ? "bg-warn-soft" : "bg-card")}
    >
      <span className="text-6xl">{r.icon}</span>
      <TileLabel>{r.title}</TileLabel>
      {waiting && !undo.isPending ? (
        <TileMark className="bg-warn">
          <Hourglass className="size-5" />
        </TileMark>
      ) : showChecked ? (
        <TileMark className="bg-brand">
          <Check className="size-7" strokeWidth={3} />
        </TileMark>
      ) : status === "rejected" ? (
        <TileMark className="bg-warn">
          <RotateCcw className="size-5" />
        </TileMark>
      ) : (
        // An empty checkmark waiting to be filled in by a tap.
        <TileMark className="border-[3px] border-dashed border-brand bg-transparent text-brand/50">
          <Check className="size-6" strokeWidth={3} />
        </TileMark>
      )}
    </button>
  );
}

/** Tells a pre-reader what the list is for, in a picture and out loud. */
function TapHint() {
  return (
    <button type="button" onClick={() => speak("Tap a picture when you're done!")} className="-mt-1 mb-3 flex items-center gap-2 px-1 text-left text-lg font-extrabold text-muted">
      <span className="animate-tap-nudge text-3xl" aria-hidden>
        👆
      </span>
      Tap when you're done!
    </button>
  );
}

function JobTile({ job }: { job: KidJobDTO }) {
  const { family } = useSession();
  const [open, setOpen] = useState(false);
  const done = { success: "Sent to a parent! 🎉", onSuccess: () => setOpen(false) };
  const claim = useAction(() => api.post(`/jobs/${job.id}/claim`, { submit: true }), done);
  const submit = useAction(() => api.post(`/job-runs/${job.activeRun!.id}/submit`, {}), done);
  const run = job.activeRun;
  const waiting = run?.status === "submitted";
  const locked = job.locked && !run;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (locked) speak("Do your list first!");
          else if (waiting) speak(`${job.title}. A parent will check it.`);
          else {
            speak(`${job.title}. ${job.description ?? ""}`);
            setOpen(true);
          }
        }}
        className={cx(TILE, locked ? "bg-locked-soft" : waiting ? "bg-warn-soft" : "bg-coin-soft")}
      >
        <span className={cx("text-6xl", locked && "grayscale")}>{job.icon}</span>
        <TileLabel>{job.title}</TileLabel>
        <Points value={job.reward} family={family} className="text-lg text-coin-ink" />
        {locked && (
          <TileMark className="bg-locked">
            <Lock className="size-5" />
          </TileMark>
        )}
        {waiting && (
          <TileMark className="bg-warn">
            <Hourglass className="size-5" />
          </TileMark>
        )}
      </button>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={job.title}
        footer={
          <Button block size="lg" variant="coin" className="py-5 text-2xl" loading={claim.isPending || submit.isPending} onClick={() => (run ? submit.mutate() : claim.mutate())}>
            <Check className="size-7" strokeWidth={3} /> I did it!
          </Button>
        }
      >
        <div className="py-4 text-center">
          <div className="text-8xl">{job.icon}</div>
          {job.description && <p className="mt-3 text-lg font-semibold text-muted">{job.description}</p>}
          <Points value={job.reward} family={family} className="mt-3 text-4xl text-coin-ink" />
        </div>
      </Sheet>
    </>
  );
}

/** The thing they're working toward, as a picture filling up: their goal, or the next prize in the store. */
function NextPrize({ available }: { available: number }) {
  const { user } = useSession();
  const summary = useKidSummary(user.id);
  const store = useStore();
  const goal = summary.data?.primaryGoal;
  const next = (store.data?.items ?? []).filter((i) => i.price > available && (i.stock === null || i.stock > 0)).sort((a, b) => a.price - b.price)[0];
  const prize = goal ? { icon: goal.icon, name: goal.name, progress: goal.progress } : next ? { icon: next.icon, name: next.name, progress: Math.max(0, available) / next.price } : null;
  if (!prize) return null;
  return (
    <div className="mt-4 flex items-center gap-3">
      <span className="text-5xl" role="img" aria-label={prize.name}>
        {prize.icon}
      </span>
      <ProgressBar value={prize.progress} color="#ffd36e" className="h-6 flex-1 bg-white/25 ring-0" />
    </div>
  );
}

export function LittleToday() {
  const { user, family } = useSession();
  const summary = useKidSummary(user.id);
  const resp = useKidResponsibilities();
  const jobs = useKidJobs();
  const celebrate = useUnlockCelebration(resp.data?.gate, () => speak("All done! Hooray!"));

  if (!summary.data || !resp.data || !jobs.data) return <PageLoader />;
  const s = summary.data;
  const due = resp.data.items.filter((i) => i.due);
  const anyLeft = due.some((i) => i.completion?.status !== "done" && i.completion?.status !== "approved");
  const list = jobs.data.jobs.filter((j) => j.activeRun || j.available || j.locked);

  return (
    <div className="pt-[calc(env(safe-area-inset-top)+16px)] md:pt-8">
      {celebrate > 0 && <Confetti key={celebrate} />}
      <button type="button" onClick={() => speak(`Hi ${user.name}! You have ${Math.max(0, s.balance)} ${family.currencyName}.`)} className="flex items-center gap-3 text-left">
        <Avatar member={user} size="lg" />
        <h1 className="text-4xl font-black tracking-tight">Hi {user.name}!</h1>
      </button>

      <Card className="mt-4 bg-gradient-to-br from-brand to-brand-strong p-5 text-on-brand ring-0">
        <div className="flex justify-center">
          <Points value={Math.max(0, s.balance)} family={family} className="text-6xl" />
        </div>
        <NextPrize available={s.available} />
      </Card>

      {due.length > 0 && (
        <>
          <Heading>My list</Heading>
          {anyLeft && <TapHint />}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {due.map((i) => (
              <ResponsibilityTile key={i.responsibility.id} item={i} />
            ))}
          </div>
        </>
      )}

      {list.length > 0 && (
        <>
          <Heading>Earn more</Heading>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {list.map((j) => (
              <JobTile key={j.id} job={j} />
            ))}
          </div>
        </>
      )}

      {due.length === 0 && list.length === 0 && (
        <div className="mt-10 text-center">
          <div className="text-8xl">🎉</div>
          <p className="mt-3 text-2xl font-black">All done today!</p>
        </div>
      )}
    </div>
  );
}

// ---------- shop ----------

function PrizeTile({ item, available }: { item: StoreItemDTO; available: number }) {
  const { family } = useSession();
  const [open, setOpen] = useState(false);
  const affordable = available >= item.price;
  const soldOut = item.stock !== null && item.stock <= 0;
  const buy = useAction(() => api.post(`/store/${item.id}/redeem`, {}), {
    success: "Asked a parent! 🎉",
    onSuccess: () => {
      speak("Good choice! A parent will say yes or not now.");
      setOpen(false);
    },
  });
  if (soldOut) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          speak(affordable ? `${item.name}. You can get this!` : `${item.name}. Keep earning!`);
          setOpen(true);
        }}
        className={cx(TILE, affordable ? "bg-coin-soft" : "bg-card")}
      >
        <span className={cx("text-6xl", !affordable && "opacity-50 grayscale")}>{item.icon}</span>
        <TileLabel>{item.name}</TileLabel>
        {affordable ? (
          <Points value={item.price} family={family} className="text-lg text-coin-ink" />
        ) : (
          <ProgressBar value={Math.max(0, available) / item.price} color="var(--coin)" className="mt-1 h-3 w-4/5" />
        )}
      </button>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={item.name}
        footer={
          affordable ? (
            <Button block size="lg" variant="coin" className="py-5 text-2xl" loading={buy.isPending} onClick={() => buy.mutate()}>
              I want this!
            </Button>
          ) : (
            <Button block size="lg" variant="secondary" className="py-5 text-2xl" onClick={() => setOpen(false)}>
              Keep earning
            </Button>
          )
        }
      >
        <div className="py-4 text-center">
          <div className="text-8xl">{item.icon}</div>
          <Points value={item.price} family={family} className="mt-3 text-4xl text-coin-ink" />
          {!affordable && <ProgressBar value={Math.max(0, available) / item.price} color="var(--coin)" className="mt-4 h-5" />}
        </div>
      </Sheet>
    </>
  );
}

const LITTLE_TIME_CHUNK = 15;

/** Screen time in fixed chunks: get some more, press play. */
function TimeTile({ rt, bank, available, receivedAt }: { rt: RewardTypeDTO; bank: BankDTO; available: number; receivedAt: number }) {
  const { family } = useSession();
  const left = useSessionClock(bank.session, receivedAt);
  const running = !!bank.session && left !== null && left > 0;
  const cost = pointsForUnits(LITTLE_TIME_CHUNK, rt.pointsPerUnit!);
  const caps = [bank.balance, bank.remainingThisWeek ?? Infinity, rt.dailyCap !== null ? Math.max(0, rt.dailyCap - bank.usedToday) : Infinity];
  const usable = Math.floor(Math.min(...caps));
  const get = useAction(() => api.post(`/rewards/${rt.id}/convert`, { units: LITTLE_TIME_CHUNK }), {
    success: rt.requiresApproval ? "Asked a parent! 🎉" : `${formatDuration(LITTLE_TIME_CHUNK)} added! 🎉`,
  });
  // Connected consoles get their time granted on the device; everything else counts down here.
  const start = useAction(() => (rt.integrationId ? api.post(`/rewards/${rt.id}/use`, { units: usable }) : api.post(`/rewards/${rt.id}/start`)), {
    onSuccess: () => speak("Have fun!"),
  });
  return (
    <Card className="col-span-2 flex items-center gap-4 p-4 sm:col-span-3">
      <span className="text-6xl">{rt.icon}</span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-lg font-extrabold">{rt.name}</div>
        <div className="text-2xl font-black tabular-nums">{running ? formatRemaining(left) : formatDuration(bank.balance)}</div>
      </div>
      {running && bank.session ? (
        <StopTimeButton session={bank.session} label="Stop" />
      ) : usable >= 1 && bank.scheduleAllowedNow ? (
        <Button size="lg" variant="coin" aria-label={`Start ${rt.name}`} onClick={() => start.mutate()} loading={start.isPending}>
          <Play className="size-7" />
        </Button>
      ) : (
        <Button size="lg" disabled={available < cost || !bank.scheduleAllowedNow} loading={get.isPending} onClick={() => get.mutate()}>
          + <Points value={cost} family={family} />
        </Button>
      )}
    </Card>
  );
}

export function LittleShop() {
  const { user, family } = useSession();
  const summary = useKidSummary(user.id);
  const rewards = useRewardTypes();
  const banks = useBanks();
  const store = useStore();
  const requests = useRedemptions();

  if (!summary.data || !rewards.data || !banks.data || !store.data) return <PageLoader />;
  const available = summary.data.available;
  const times = rewards.data.rewardTypes.filter((t) => t.active && isConvertible(t) && t.kind === "time");
  const waiting = (requests.data?.redemptions ?? []).filter((r) => r.status === "pending" || r.status === "approved");

  return (
    <div className="pt-[calc(env(safe-area-inset-top)+16px)] md:pt-8">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-black tracking-tight">Shop</h1>
        <Points value={Math.max(0, available)} family={family} className="text-4xl text-coin-ink" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {times.map((rt) => {
          const bank = banks.data.banks.find((b) => b.rewardTypeId === rt.id);
          return bank ? <TimeTile key={rt.id} rt={rt} bank={bank} available={available} receivedAt={banks.dataUpdatedAt} /> : null;
        })}
        {store.data.items.map((item) => (
          <PrizeTile key={item.id} item={item} available={available} />
        ))}
      </div>
      {times.length === 0 && store.data.items.length === 0 && (
        <div className="mt-10 text-center">
          <div className="text-8xl">🛍️</div>
          <p className="mt-3 text-xl font-black">Ask a parent to add prizes!</p>
        </div>
      )}
      {waiting.length > 0 && (
        <div className="mt-6 space-y-2">
          {waiting.map((r) => (
            <Card key={r.id} className="flex items-center gap-3 p-3">
              {r.status === "pending" ? <Hourglass className="size-7 text-warn" /> : <Check className="size-7 text-brand" strokeWidth={3} />}
              <span className="min-w-0 flex-1 truncate text-lg font-extrabold">{r.title}</span>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- me ----------

export function LittleMe() {
  const { user } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const [addingPasskey, setAddingPasskey] = useState(false);
  const [muted, setMuted] = useState(isSpeechMuted);
  const pick = useAction((avatar: string) => api.patch(`/family/kids/${user.id}`, { avatar }));

  return (
    <div className="pt-[calc(env(safe-area-inset-top)+16px)] md:pt-8">
      <div className="text-center">
        <Avatar member={user} size="xl" />
        <h1 className="mt-2 text-4xl font-black tracking-tight">{user.name}</h1>
      </div>
      <div className="mt-6 grid grid-cols-4 gap-2">
        {AVATARS.slice(0, 16).map((a) => (
          <button
            key={a}
            type="button"
            aria-label={`Pick ${a}`}
            aria-pressed={user.avatar === a}
            onClick={() => pick.mutate(a)}
            className={cx("flex aspect-square items-center justify-center rounded-3xl text-4xl transition active:scale-90", user.avatar === a ? "bg-brand-soft ring-4 ring-brand" : "bg-card ring-1 ring-line")}
          >
            {a}
          </button>
        ))}
      </div>
      <Button
        className="mt-6"
        block
        size="lg"
        variant="secondary"
        aria-label={muted ? "Turn reading aloud on" : "Turn reading aloud off"}
        onClick={() => {
          setSpeechMuted(!muted);
          setMuted(!muted);
        }}
      >
        {muted ? <VolumeX className="size-7" /> : <Volume2 className="size-7" />}
      </Button>
      <div className="mt-10 space-y-2">
        {passkeysSupported() && (
          <Button variant="ghost" block onClick={() => setAddingPasskey(true)}>
            <Fingerprint className="size-4" /> Add a passkey
          </Button>
        )}
        <Button
          variant="ghost"
          block
          onClick={async () => {
            await logout();
            navigate("/kid-login");
          }}
        >
          <LogOut className="size-4" /> Switch user
        </Button>
      </div>
      <AddKidPasskeySheet open={addingPasskey} onClose={() => setAddingPasskey(false)} />
    </div>
  );
}
